import React from "react";

interface Props {
  currentPage: number;
  totalPages: number;
  label: string;
  onChange: (page: number) => void;
}

// Condenses a long run of pages to first / last / a window around the current
// one, with gaps marked by an ellipsis: 1 … 4 5 6 … 20.
export const pageItems = (
  currentPage: number,
  totalPages: number,
): Array<number | "gap"> => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const window = [currentPage - 1, currentPage, currentPage + 1].filter(
    (page) => page > 1 && page < totalPages,
  );
  const pages = Array.from(new Set([1, ...window, totalPages])).sort(
    (a, b) => a - b,
  );

  return pages.reduce<Array<number | "gap">>((items, page, index) => {
    if (index > 0 && page - (pages[index - 1] as number) > 1) {
      items.push("gap");
    }
    items.push(page);

    return items;
  }, []);
};

const Pagination = ({ currentPage, totalPages, label, onChange }: Props) => {
  if (totalPages <= 1) {
    return null;
  }

  return (
    <nav className="pagination" aria-label={label}>
      <button
        className="page-chevron"
        type="button"
        aria-label="Previous page"
        disabled={currentPage <= 1}
        onClick={() => onChange(currentPage - 1)}
      >
        <svg aria-hidden="true">
          <use href="#icon-chevron-l" />
        </svg>
      </button>

      {pageItems(currentPage, totalPages).map((item, index) =>
        item === "gap" ? (
          <span className="page-gap" aria-hidden="true" key={`gap-${index}`}>
            &hellip;
          </span>
        ) : (
          <button
            className={`page-btn ${item === currentPage ? "is-current" : ""}`}
            type="button"
            key={item}
            aria-current={item === currentPage ? "page" : undefined}
            aria-label={`Page ${item}`}
            onClick={() => onChange(item)}
          >
            {item}
          </button>
        ),
      )}

      <button
        className="page-chevron"
        type="button"
        aria-label="Next page"
        disabled={currentPage >= totalPages}
        onClick={() => onChange(currentPage + 1)}
      >
        <svg aria-hidden="true">
          <use href="#icon-chevron-r" />
        </svg>
      </button>
    </nav>
  );
};

export default Pagination;
